// The job bus: how a sweep hands a league's due work to whatever runs it.
//
// ICD-S09. A league's message is ONE LEAGUE's due job ids, and that is the
// whole of the design for the clock's work: a league's jobs run one after
// another in the clock's order (ICD-S05 §7.6), so the unit a consumer takes
// is a league and never a job, while two leagues' messages run in parallel
// freely because nothing a job writes crosses a league. Delivery is at least
// once; the ledger's conditional claim makes a second delivery run nothing
// (§4.13). The other message is a pump run of the push wave (ICD-S04 §8),
// which names no league: it claims a page of due deliveries and sends it.
//
// TWO IMPLEMENTATIONS. `queue` in the ingest worker, over a Cloudflare Queue,
// is production's. Inline is the clock service's own: given no bus, a sweep
// runs each message itself before returning, which is how the conformance
// suite, the season harness and a local script see the whole of a minute in
// one call. A `recordingBus` is for a check that wants to see what was sent
// without running it.

import type { JobOutcomeShape } from '@fsp/domain';
import { z } from 'zod';

/** One league's due work, as sent. `at` is the sweep's instant. */
export interface LeagueWork {
  leagueId: string;
  jobIds: readonly string[];
  at: string;
}

/**
 * The message as it crosses the Queue, validated at the boundary: a consumer
 * that cannot read a message says so rather than running on a guess.
 */
export const LeagueWorkMessage = z.object({
  leagueId: z.string().min(1),
  jobIds: z.array(z.string().min(1)),
  at: z.string().min(1),
});

/**
 * One run of the push pump, as sent: the wave (DATA_LAYER_DESIGN §4.6). The
 * sweep's own pump pass claims a page; when the page was full it asks how
 * many more are due and sends one of these per page, so a thousand pushes
 * leave in parallel consumer runs rather than fifty a minute.
 */
export interface PumpWork {
  pump: 'push';
  /** Deliveries this run may claim. */
  limit: number;
  /** The sweep's instant. */
  at: string;
}

export const PumpWorkMessage = z.object({
  pump: z.literal('push'),
  limit: z.number().int().positive(),
  at: z.string().min(1),
});

export type WorkMessage = LeagueWork | PumpWork;

/** Every message the bus carries, told apart by its shape. */
export const WorkMessageSchema = z.union([LeagueWorkMessage, PumpWorkMessage]);

export interface JobBus {
  /** Hand these over. Resolves once the transport has accepted them, not once they have run. */
  send(work: readonly WorkMessage[]): Promise<void>;
}

/** What a sweep did: how much it sent, and what ran when the bus was inline. */
export interface SweepReport {
  /** Due rows sent. */
  enqueued: number;
  /** Messages sent: one per league with ready rows. */
  leagues: number;
  /** The outcomes of the jobs run, when the sweep ran them itself (no bus). Empty otherwise. */
  outcomes: JobOutcomeShape[];
}

/** A bus that keeps what it was given, for a check to read. League messages only: the clock sends nothing else. */
export function recordingBus(): JobBus & { sent: LeagueWork[] } {
  const sent: LeagueWork[] = [];
  return {
    sent,
    async send(work) {
      for (const w of work) {
        if ('leagueId' in w) sent.push({ ...w, jobIds: [...w.jobIds] });
      }
    },
  };
}
